import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { EmptyState, StatusPill, type StatusLevel } from '@/lib';
import { useApiConfig } from '@/app/ApiProvider';
import { apiFetch } from '@/app/api-fetch';
import { goAlert, goAsset, goFleet } from '@/app/navigation';
import type { InboxItem } from '@/mocks/fixtures';
import type { S10Filters } from './filters-state';

type InboxResponse = { items: InboxItem[] };

function useInbox() {
  const config = useApiConfig();
  return useQuery({
    queryKey: ['inbox'],
    queryFn: async (): Promise<InboxItem[]> => {
      const res = await apiFetch(config, '/v1/inbox');
      const body = (await res.json()) as InboxResponse;
      return body.items;
    },
    staleTime: 30_000,
  });
}

const KIND_LABEL: Record<InboxItem['kind'], string> = {
  alert: 'Alert',
  mention: 'Mention',
  subscription: 'Subscription',
};

export function ActivityTab({ filters }: { filters: S10Filters }) {
  const navigate = useNavigate();
  const inbox = useInbox();

  if (inbox.isLoading) return <ListSkeleton />;
  if (inbox.isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load inbox"
        body="Retry, or check the alert-service status."
        errorCode={String(inbox.error)}
        cta={{ label: 'Retry', onClick: () => inbox.refetch() }}
      />
    );
  }
  const all = inbox.data ?? [];
  const items = all.filter((i) => {
    if (filters.unreadOnly && i.read) return false;
    if (filters.kinds.length > 0 && !filters.kinds.includes(i.kind)) return false;
    return true;
  });
  const noFilters = !filters.unreadOnly && filters.kinds.length === 0;
  if (items.length === 0) {
    return (
      <EmptyState
        variant="empty"
        title={noFilters ? 'Inbox empty' : 'No items match'}
        body={
          noFilters
            ? 'No alerts, mentions, or subscription updates.'
            : 'Adjust the Filters tab or clear filters to see everything.'
        }
        cta={{ label: 'View fleet', onClick: () => goFleet(navigate) }}
      />
    );
  }
  const unread = items.filter((i) => !i.read).length;
  const countLine = noFilters
    ? `${unread} unread · ${items.length} total`
    : `${unread} unread · ${items.length} of ${all.length} shown`;

  return (
    <div className="flex flex-col gap-sp-3">
      <p className="text-fs-meta text-gray-700">{countLine}</p>
      <ul className="flex flex-col gap-sp-2">
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() =>
                item.alert_id
                  ? goAlert(navigate, item.alert_id)
                  : goAsset(navigate, item.asset_id)
              }
              data-read={item.read || undefined}
              className="flex w-full items-start gap-sp-3 rounded-r-default border border-gray-300 bg-gray-50 px-sp-4 py-sp-3 text-left hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-info data-[read=true]:bg-gray-100"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-sp-2">
                  {!item.read ? (
                    <span
                      aria-label="Unread"
                      className="inline-block h-sp-2 w-sp-2 rounded-r-chip bg-info"
                    />
                  ) : null}
                  <p className="truncate text-fs-body font-medium text-gray-900">
                    {item.title}
                  </p>
                </div>
                <p className="mt-sp-1 text-fs-meta text-gray-700">{item.body}</p>
                <p className="mt-sp-1 font-mono text-fs-meta text-gray-700">
                  {KIND_LABEL[item.kind]} · {item.asset_name} ·{' '}
                  {relativeAgo(item.received_at)}
                </p>
              </div>
              <StatusPill
                level={item.severity as StatusLevel}
                label={item.severity}
              />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-sp-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="h-20 animate-pulse rounded-r-default bg-gray-100"
        />
      ))}
    </div>
  );
}

function relativeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}
